import { useMemo } from 'react';
import {
  buildMindmapLayout,
  buildParentMap,
  type LayoutResult,
} from './layout-mindmap';
import {
  getHighlightedMindmapNodeIds,
  getVisibleNodeIds,
  getVisibleNodeYById,
} from './mindmap-derived-model';
import type { ParsedMindmap } from './mindmap-types';

interface UseMindmapGraphModelInput {
  expandedNodeIds: Set<string>;
  hoveredNodeId: string | null;
  parsed: ParsedMindmap | null;
}

interface UseMindmapGraphModelResult {
  highlightedNodeIds: Set<string>;
  layout: LayoutResult | null;
  parentMap: Map<string, string>;
  visibleNodeIds: Set<string>;
  visibleNodeYById: Map<string, number>;
}

export function useMindmapGraphModel(
  input: UseMindmapGraphModelInput,
): UseMindmapGraphModelResult {
  const {
    expandedNodeIds,
    hoveredNodeId,
    parsed,
  } = input;

  const parentMap = useMemo(() => {
    if (!parsed) {
      return new Map<string, string>();
    }

    return buildParentMap(parsed.root);
  }, [parsed]);

  const layout = useMemo(() => {
    if (!parsed) {
      return null;
    }

    return buildMindmapLayout(parsed.root, expandedNodeIds);
  }, [parsed, expandedNodeIds]);

  const visibleNodeIds = useMemo(() => {
    return getVisibleNodeIds(layout);
  }, [layout]);

  const visibleNodeYById = useMemo(() => {
    return getVisibleNodeYById(layout);
  }, [layout]);

  const highlightedNodeIds = useMemo(() => {
    return getHighlightedMindmapNodeIds({
      expandedNodeIds,
      hoveredNodeId,
      parentMap,
      root: parsed?.root ?? null,
    });
  }, [expandedNodeIds, hoveredNodeId, parentMap, parsed]);

  return {
    highlightedNodeIds,
    layout,
    parentMap,
    visibleNodeIds,
    visibleNodeYById,
  };
}
